import React, {useState, useEffect} from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Button, Row, Col, Card, Modal, Spinner } from 'react-bootstrap'
import Loader from '../components/Loader'
import Message from '../components/Message'
import CourseAutocomplete from '../components/CourseAutocomplete'
import { updateUserProfileDetails, getUserDetails, uploadProfileImage } from '../actions/userActions'
import { USER_UPDATE_PROFILE_RESET, USER_PROFILE_IMAGE_RESET } from '../constants/userConstants'
import { listPaymentTypes, listUserPaymentMethods, addUserPaymentMethod, deleteUserPaymentMethod } from '../actions/paymentActions'
import { USER_PAYMENT_METHOD_ADD_RESET } from '../constants/paymentConstants'


function ProfileScreen() {
    // ── Account fields
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // ── Golf / Profile fields
    const [handicap, setHandicap] = useState('')
    const [dominantHand, setDominantHand] = useState('')
    const [homeCourse, setHomeCourse] = useState(null) // { id, name }

    // ── Personal fields
    const [birthDate, setBirthDate] = useState('')
    const [birthPlace, setBirthPlace] = useState('')
    const [country, setCountry] = useState('')
    const [college, setCollege] = useState('')

    // ── Clothing / sizing fields
    const [shirtSize, setShirtSize] = useState('')
    const [pantWaist, setPantWaist] = useState('')
    const [pantInseam, setPantInseam] = useState('')
    const [shoeSizeUs, setShoeSizeUs] = useState('')
    const [shoeWidth, setShoeWidth] = useState('')
    const [gloveSize, setGloveSize] = useState('')

    const [sizeModal, setSizeModal] = useState(null)
    const [hoveredSizing, setHoveredSizing] = useState(null)
    const [showPasswordModal, setShowPasswordModal] = useState(false)
    const [savingSection, setSavingSection] = useState(null)

    // ── Payment method fields
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [newPaymentTypeId, setNewPaymentTypeId] = useState('')
    const [newAccountDetails, setNewAccountDetails] = useState('')

    const [messageError, setMessageError] = useState('')
    const [messageSuccess, setMessageSuccess] = useState('')

    // ── Profile image
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState('')

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const userDetails = useSelector(state => state.userDetails)
    const { loading, error, user } = userDetails

    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin

    const userUpdateProfile = useSelector(state => state.userUpdateProfile)
    const { success } = userUpdateProfile

    const paymentTypeList = useSelector(state => state.paymentTypeList)
    const { paymentTypes = [] } = paymentTypeList

    const userPaymentMethodList = useSelector(state => state.userPaymentMethodList)
    const { methods: paymentMethods = [] } = userPaymentMethodList

    const userPaymentMethodAdd = useSelector(state => state.userPaymentMethodAdd)
    const { loading: paymentAddLoading, success: addSuccess } = userPaymentMethodAdd

    const userPaymentMethodDelete = useSelector(state => state.userPaymentMethodDelete)
    const { loading: paymentDeleteLoading, success: deleteSuccess } = userPaymentMethodDelete

    const profileImageUpload = useSelector(state => state.profileImageUpload)
    const { loading: imageUploading, success: imageUploadSuccess } = profileImageUpload

    useEffect(() => {
        if (!userInfo) {
            navigate('/login')
        } else {
            dispatch(listPaymentTypes())
            dispatch(listUserPaymentMethods())
            if (!user || !user.name || success) {
                dispatch({ type: USER_UPDATE_PROFILE_RESET })
                dispatch(getUserDetails('profile'))
            } else {
                setName(user.name)
                setEmail(user.email)
                setHandicap(user.handicap !== null && user.handicap !== undefined ? user.handicap : '')
                setDominantHand(user.dominant_hand || '')
                setHomeCourse(user.home_course_id ? { id: user.home_course_id, name: user.home_course_name } : null)
                setBirthDate(user.birth_date || '')
                setBirthPlace(user.birth_place || '')
                setCountry(user.country || '')
                setCollege(user.college || '')
                setShirtSize(user.shirt_size || '')
                setPantWaist(user.pant_waist !== null && user.pant_waist !== undefined ? user.pant_waist : '')
                setPantInseam(user.pant_inseam !== null && user.pant_inseam !== undefined ? user.pant_inseam : '')
                setShoeSizeUs(user.shoe_size_us || '')
                setShoeWidth(user.shoe_width || '')
                setGloveSize(user.glove_size || '')
            }
        }
    }, [userInfo, user, navigate, success, dispatch])

    useEffect(() => {
        if (addSuccess) {
            dispatch({ type: USER_PAYMENT_METHOD_ADD_RESET })
            dispatch(listUserPaymentMethods())
            setShowPaymentModal(false)
            setNewPaymentTypeId('')
            setNewAccountDetails('')
        }
    }, [addSuccess, dispatch])

    useEffect(() => {
        if (deleteSuccess) {
            dispatch(listUserPaymentMethods())
        }
    }, [deleteSuccess, dispatch])

    useEffect(() => {
        if (imageUploadSuccess) {
            dispatch({ type: USER_PROFILE_IMAGE_RESET })
            setSavingSection(null)
            setImageFile(null)
            setImagePreview('')
            dispatch(getUserDetails('profile'))
        }
    }, [imageUploadSuccess, dispatch])

    useEffect(() => {
        if (success) {
            setSavingSection(null)
            setSizeModal(null)
            setShowPasswordModal(false)
            setPassword('')
            setConfirmPassword('')
        }
    }, [success])

    const handleAddPayment = () => {
        if (!newPaymentTypeId || !newAccountDetails.trim()) return
        dispatch(addUserPaymentMethod(newPaymentTypeId, newAccountDetails.trim()))
    }

    const handleDeletePayment = (id) => {
        dispatch(deleteUserPaymentMethod(id))
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
    }

    const handleImageSave = () => {
        if (!imageFile) return
        setSavingSection('image')
        dispatch(uploadProfileImage(imageFile))
    }

    const PAYMENT_ICONS = {
        'Venmo':       'fa-solid fa-mobile-screen',
        'Zelle':       'fa-solid fa-bolt',
        'PayPal':      'fa-solid fa-p',
        'Cash':        'fa-solid fa-money-bill-wave',
        'Credit Card': 'fa-solid fa-credit-card',
    }

    const COUNTRY_LABELS = {
        ARG: 'Argentina', AUS: 'Australia', AUT: 'Austria', BEL: 'Belgium',
        CAN: 'Canada', TPE: 'Chinese Taipei', COL: 'Colombia', DEN: 'Denmark',
        ENG: 'England', FIJ: 'Fiji', FIN: 'Finland', FRA: 'France',
        GER: 'Germany', IRL: 'Ireland', JPN: 'Japan', MEX: 'Mexico',
        NZL: 'New Zealand', NIR: 'Northern Ireland', NOR: 'Norway',
        CHN: "People's Rep of China", PHL: 'Philippines', PUR: 'Puerto Rico',
        KOR: 'Republic of Korea', SCO: 'Scotland', RSA: 'South Africa',
        SWE: 'Sweden', USA: 'United States', VEN: 'Venezuela',
    }

    const playerAge = birthDate
        ? Math.floor((new Date() - new Date(birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
        : null

    const sizingConfig = [
        {
            key: 'shirtSize', label: 'Shirt Size', icon: 'fa-solid fa-shirt',
            value: shirtSize, setValue: setShirtSize,
            options: [{ value: '', label: '-- Select --' }, { value: 'M', label: 'M' }, { value: 'L', label: 'L' }, { value: 'XL', label: 'XL' }, { value: 'XXL', label: 'XXL' }, { value: 'LT', label: 'LT' }],
        },
        {
            key: 'pantWaist', label: 'Pant Waist', icon: 'fa-solid fa-ruler',
            value: pantWaist, setValue: setPantWaist,
            options: [{ value: '', label: '-- Select --' }, { value: '26', label: '26"' }, { value: '28', label: '28"' }, { value: '30', label: '30"' }, { value: '32', label: '32"' }, { value: '34', label: '34"' }, { value: '36', label: '36"' }, { value: '38', label: '38"' }, { value: '40', label: '40"' }, { value: '42', label: '42"' }, { value: '44', label: '44"' }],
        },
        {
            key: 'pantInseam', label: 'Pant Inseam', icon: 'fa-solid fa-arrows-up-down',
            value: pantInseam, setValue: setPantInseam,
            options: [{ value: '', label: '-- Select --' }, { value: '28', label: '28"' }, { value: '29', label: '29"' }, { value: '30', label: '30"' }, { value: '31', label: '31"' }, { value: '32', label: '32"' }, { value: '33', label: '33"' }, { value: '34', label: '34"' }, { value: '35', label: '35"' }, { value: '36', label: '36"' }],
        },
        {
            key: 'shoeSizeUs', label: 'Shoe Size (US)', icon: 'fa-solid fa-shoe-prints',
            value: shoeSizeUs, setValue: setShoeSizeUs,
            options: [{ value: '', label: '-- Select --' }, { value: '6', label: '6' }, { value: '6.5', label: '6.5' }, { value: '7', label: '7' }, { value: '7.5', label: '7.5' }, { value: '8', label: '8' }, { value: '8.5', label: '8.5' }, { value: '9', label: '9' }, { value: '9.5', label: '9.5' }, { value: '10', label: '10' }, { value: '10.5', label: '10.5' }, { value: '11', label: '11' }, { value: '11.5', label: '11.5' }, { value: '12', label: '12' }, { value: '12.5', label: '12.5' }, { value: '13', label: '13' }, { value: '14', label: '14' }, { value: '15', label: '15' }],
        },
        {
            key: 'shoeWidth', label: 'Shoe Width', icon: 'fa-solid fa-arrows-left-right',
            value: shoeWidth, setValue: setShoeWidth,
            options: [{ value: '', label: '-- Select --' }, { value: 'D', label: 'D' }, { value: 'EE', label: 'EE' }, { value: 'Wide', label: 'Wide' }, { value: 'Narrow', label: 'Narrow' }],
        },
        {
            key: 'gloveSize', label: 'Glove / Dominant', icon: 'fa-solid fa-hand',
            value: gloveSize, setValue: setGloveSize,
            options: [{ value: '', label: '-- Select --' }, { value: 'S', label: 'S' }, { value: 'M', label: 'M' }, { value: 'L', label: 'L' }, { value: 'XL', label: 'XL' }, { value: 'Cadet M', label: 'Cadet M' }],
            extraFields: [
                {
                    label: 'Dominant Hand',
                    value: dominantHand, setValue: setDominantHand,
                    options: [{ value: '', label: '-- Select --' }, { value: 'Right', label: 'Right' }, { value: 'Left', label: 'Left' }],
                },
            ],
        },
        {
            key: 'handicap', label: 'Handicap', icon: 'fa-solid fa-golf-ball-tee', type: 'number',
            value: handicap, setValue: setHandicap,
            options: [],
        },
        {
            key: 'birthday', label: 'Birthday', icon: 'fa-solid fa-cake-candles', type: 'date',
            value: birthDate, setValue: setBirthDate,
            options: [],
            extraFields: [
                { label: 'Birth Place', type: 'text', placeholder: 'City, State', value: birthPlace, setValue: setBirthPlace },
                {
                    label: 'Country', value: country, setValue: setCountry,
                    options: [
                        { value: '', label: '-- Select Country --' },
                        { value: 'ARG', label: 'Argentina' },
                        { value: 'AUS', label: 'Australia' },
                        { value: 'AUT', label: 'Austria' },
                        { value: 'BEL', label: 'Belgium' },
                        { value: 'CAN', label: 'Canada' },
                        { value: 'TPE', label: 'Chinese Taipei' },
                        { value: 'COL', label: 'Colombia' },
                        { value: 'DEN', label: 'Denmark' },
                        { value: 'ENG', label: 'England' },
                        { value: 'FIJ', label: 'Fiji' },
                        { value: 'FIN', label: 'Finland' },
                        { value: 'FRA', label: 'France' },
                        { value: 'GER', label: 'Germany' },
                        { value: 'IRL', label: 'Ireland' },
                        { value: 'JPN', label: 'Japan' },
                        { value: 'MEX', label: 'Mexico' },
                        { value: 'NZL', label: 'New Zealand' },
                        { value: 'NIR', label: 'Northern Ireland' },
                        { value: 'NOR', label: 'Norway' },
                        { value: 'CHN', label: "People's Rep of China" },
                        { value: 'PHL', label: 'Philippines' },
                        { value: 'PUR', label: 'Puerto Rico' },
                        { value: 'KOR', label: 'Republic of Korea' },
                        { value: 'SCO', label: 'Scotland' },
                        { value: 'RSA', label: 'South Africa' },
                        { value: 'SWE', label: 'Sweden' },
                        { value: 'USA', label: 'United States' },
                        { value: 'VEN', label: 'Venezuela' },
                    ],
                },
            ],
        },
        {
            key: 'college', label: 'Education', icon: 'fa-solid fa-graduation-cap',
            type: 'text', placeholder: 'e.g. (UNLV) University of Nevada, Las Vegas',
            hint: 'e.g. (UNLV) University of Nevada, Las Vegas',
            value: college, setValue: setCollege,
            options: [],
        },
    ]

    const buildProfilePayload = () => ({
        id: user._id,
        name,
        email,
        password: '',
        handicap: handicap !== '' ? handicap : null,
        dominant_hand: dominantHand,
        birth_date: birthDate || null,
        birth_place: birthPlace,
        country,
        college,
        shirt_size: shirtSize,
        pant_waist: pantWaist !== '' ? pantWaist : null,
        pant_inseam: pantInseam !== '' ? pantInseam : null,
        shoe_size_us: shoeSizeUs,
        shoe_width: shoeWidth,
        glove_size: gloveSize,
        home_course_id: homeCourse?.id || null,
    })

    const credentialsSaveHandler = () => {
        if (password !== confirmPassword) {
            setMessageError('Passwords do not match')
            return
        }
        setSavingSection('credentials')
        setMessageError('')
        dispatch(updateUserProfileDetails({ ...buildProfilePayload(), password }))
    }

    const tileSaveHandler = () => {
        setSavingSection('tile')
        dispatch(updateUserProfileDetails(buildProfilePayload()))
    }

    return (
        <Row>
            <Col md={12}>
                <h2>My Account</h2>
                {loading && <Loader />}
                {messageSuccess && <Message variant='success'>{messageSuccess}</Message>}
                {messageError && <Message variant='danger'>{messageError}</Message>}
                {error && <Message variant='danger'>{error}</Message>}

                <Form>

                    {/* ── Top Row: Login Credentials + Payment Methods + Profile Image ── */}
                    <Row className='mb-4 g-4'>
                        <Col md={4}>
                            <Card className='h-100'>
                                <Card.Header className='d-flex justify-content-between align-items-center'>
                                    <strong>Login Credentials</strong>
                                    <div className='d-flex gap-2 align-items-center'>
                                        {savingSection === 'credentials' && <Spinner animation='border' size='sm' />}
                                        <Button variant='outline-secondary' size='sm' onClick={() => setShowPasswordModal(true)}>Password Reset</Button>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group controlId='name' className='mb-3'>
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='Enter name'
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group controlId='email'>
                                        <Form.Label>Email Address</Form.Label>
                                        <Form.Control
                                            type='email'
                                            placeholder='Enter email'
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </Form.Group>
                                </Card.Body>
                                <Card.Footer>
                                    <Button
                                        variant='primary'
                                        size='sm'
                                        onClick={credentialsSaveHandler}
                                        disabled={savingSection === 'credentials'}
                                    >
                                        {savingSection === 'credentials'
                                            ? <><Spinner size='sm' animation='border' className='me-1' />Saving...</>
                                            : 'Save Credentials'}
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className='h-100'>
                                <Card.Header className='d-flex justify-content-between align-items-center'>
                                    <strong>Payment Methods</strong>
                                    <div className='d-flex gap-2 align-items-center'>
                                        {(paymentAddLoading || paymentDeleteLoading) && <Spinner animation='border' size='sm' />}
                                        <Button variant='outline-primary' size='sm' onClick={() => setShowPaymentModal(true)}>
                                            <i className='fa-solid fa-plus me-1'></i> Add
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {paymentMethods.length === 0 ? (
                                        <p className='text-muted mb-0'>No payment methods added yet.</p>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                                            {paymentMethods.map(pm => (
                                                <div key={pm.id} style={{ position: 'relative', width: '200px', textAlign: 'center', padding: '1.25rem 1rem', border: '1px solid #dee2e6', borderRadius: '0.5rem', background: '#f8f9fa' }}>
                                                    <i className={`${PAYMENT_ICONS[pm.payment_type_name] || 'fa-solid fa-wallet'}`} style={{ fontSize: '2rem', color: '#0d6efd' }}></i>
                                                    <div style={{ fontWeight: '600', marginTop: '0.5rem' }}>{pm.payment_type_name}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.2rem', wordBreak: 'break-all' }}>{pm.account_details}</div>
                                                    <Button
                                                        variant='outline-danger'
                                                        size='sm'
                                                        className='mt-2'
                                                        onClick={() => handleDeletePayment(pm.id)}
                                                    >
                                                        <i className='fa-solid fa-trash'></i>
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className='h-100'>
                                <Card.Header className='d-flex justify-content-between align-items-center'>
                                    <strong>Profile Image</strong>
                                    {savingSection === 'image' && <Spinner animation='border' size='sm' />}
                                </Card.Header>
                                <Card.Body className='d-flex flex-column align-items-center justify-content-center'>
                                    <div style={{
                                        width: '150px', height: '150px',
                                        borderRadius: '50%',
                                        border: '4px solid #0d6efd',
                                        boxShadow: '0 0 0 4px rgba(13,110,253,0.15), 0 8px 24px rgba(0,0,0,0.12)',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        margin: '0 auto 1.25rem',
                                        background: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        {(imagePreview || user?.image) ? (
                                            <img
                                                src={imagePreview || user.image}
                                                alt='Profile'
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <i className='fa-solid fa-user' style={{ fontSize: '4rem', color: '#adb5bd' }}></i>
                                        )}
                                    </div>
                                    <input
                                        type='file'
                                        accept='image/*'
                                        style={{ display: 'none' }}
                                        id='profileImageInput'
                                        onChange={handleImageChange}
                                    />
                                    <label htmlFor='profileImageInput' style={{ cursor: 'pointer' }}>
                                        <Button as='span' variant='outline-primary' size='sm'>
                                            <i className='fa-solid fa-camera me-1'></i> Change Photo
                                        </Button>
                                    </label>
                                    {imageFile && (
                                        <Button
                                            variant='primary'
                                            size='sm'
                                            className='mt-2'
                                            onClick={handleImageSave}
                                            disabled={savingSection === 'image'}
                                        >
                                            {savingSection === 'image'
                                                ? <><Spinner size='sm' animation='border' className='me-1' />Uploading...</>
                                                : 'Save Photo'}
                                        </Button>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Player Card ───────────────────────────────────── */}
                    <Card className='mb-4' style={{ background: 'linear-gradient(to right, #4a5568 0%, #2d3748 35%, #1a202c 100%)', border: 'none', color: 'white', overflow: 'hidden' }}>
                        <Card.Body style={{ padding: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '220px' }}>

                                {/* Left: photo */}
                                <div className="mobileHide" style={{ width: '280px', flexShrink: 0, overflow: 'hidden', background: '#2c2f33' }}>
                                    {user?.image ? (
                                        <img  src={user.image} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className='fa-solid fa-user' style={{ fontSize: '5rem', color: '#495057' }}></i>
                                        </div>
                                    )}
                                </div>

                                {/* Right: content */}
                                <div style={{ flex: 1, padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

                                    {/* Name */}
                                    <div>
                                        <h2 style={{ fontWeight: '800', fontSize: '2.4rem', marginBottom: '1rem', lineHeight: 1.05, color: 'white' }}>
                                            {name || '—'}
                                        </h2>

                                        {/* Info row */}
                                        <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
                                            {country && (
                                                <div>
                                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Country</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: '700', color: 'white' }}>
                                                        <img src={`/flags/${country}.svg`} alt={country} style={{ width: '18px', height: '18px' }} />
                                                        {COUNTRY_LABELS[country] || country}
                                                    </div>
                                                </div>
                                            )}
                                            {playerAge !== null && (
                                                <div>
                                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Age</div>
                                                    <div style={{ fontWeight: '700', color: 'white' }}>{playerAge}</div>
                                                </div>
                                            )}
                                            {birthPlace && (
                                                <div>
                                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Birthplace</div>
                                                    <div style={{ fontWeight: '700', color: 'white' }}>{birthPlace}</div>
                                                </div>
                                            )}
                                            {college && (
                                                <div>
                                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>College</div>
                                                    <div style={{ fontWeight: '700', color: 'white' }}>{college}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Stats row */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Handicap</div>
                                            <div style={{ fontSize: '2.2rem', fontWeight: '800', lineHeight: 1, color: 'white' }}>
                                                {handicap !== '' && handicap !== null ? handicap : '—'}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Tournament Wins</div>
                                            <div style={{ fontSize: '2.2rem', fontWeight: '800', lineHeight: 1, color: '#495057' }}>—</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Best Finish</div>
                                            <div style={{ fontSize: '2.2rem', fontWeight: '800', lineHeight: 1, color: '#495057' }}>—</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6c757d', marginBottom: '0.3rem' }}>Dominant Hand</div>
                                            <div style={{ fontSize: '2.2rem', fontWeight: '800', lineHeight: 1, color: 'white' }}>{dominantHand || '—'}</div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ── Home Course ──────────────────────────────────────── */}
                    <Card className='mb-4'>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <strong><i className='fa-solid fa-flag me-2'></i>Home Course</strong>
                            {savingSection === 'homeCourse' && <Spinner animation='border' size='sm' />}
                        </Card.Header>
                        <Card.Body>
                            <CourseAutocomplete
                                value={homeCourse}
                                onChange={setHomeCourse}
                                userInfo={userInfo}
                            />
                            <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '0.4rem' }}>
                                Type to search existing courses. If yours isn't listed, type the name and click away to add it.
                            </div>
                        </Card.Body>
                        <Card.Footer>
                            <Button
                                variant='primary'
                                size='sm'
                                disabled={savingSection === 'homeCourse'}
                                onClick={() => { setSavingSection('homeCourse'); tileSaveHandler() }}
                            >
                                {savingSection === 'homeCourse'
                                    ? <><Spinner size='sm' animation='border' className='me-1' />Saving...</>
                                    : 'Save Home Course'}
                            </Button>
                        </Card.Footer>
                    </Card>

                    {/* ── Profile Details ─────────────────────────────────── */}
                    <Card className='mb-4'>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <strong>Profile Details</strong>
                            {savingSection === 'tile' && <Spinner animation='border' size='sm' />}
                        </Card.Header>
                        <Card.Body>
                            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap',  }}>
                                {sizingConfig.map(item => (
                                    <div
                                        key={item.key}
                                        style={{ position: 'relative', width: '33.2%', textAlign: 'center', padding: '1rem 0.5rem', border: '1px solid #dee2e6', borderRadius: '0.5rem', background: '#f8f9fa', cursor: 'pointer', userSelect: 'none' }}
                                        onMouseEnter={() => setHoveredSizing(item.key)}
                                        onMouseLeave={() => setHoveredSizing(null)}
                                    >
                                        <i
                                            className={item.icon}
                                            style={{ fontSize: '1.8rem', color: item.value ? '#0d6efd' : '#adb5bd' }}
                                        ></i>
                                        <div style={{ marginTop: '0.5rem', fontWeight: '600', fontSize: '0.85rem', color: item.value ? '#212529' : '#6c757d' }}>
                                            {item.value || '—'}
                                        </div>
                                        <div style={{ fontSize: '0.63rem', color: '#6c757d', marginTop: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            {item.label}
                                        </div>
                                        {hoveredSizing === item.key && (
                                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Button
                                                    variant='light'
                                                    size='sm'
                                                    onClick={(e) => { e.stopPropagation(); setSizeModal(item.key) }}
                                                >
                                                    <i className='fa-solid fa-pen me-1'></i> Edit
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ── Sizing Modal ──────────────────────────────────── */}
                    {sizeModal && (() => {
                        const item = sizingConfig.find(s => s.key === sizeModal)
                        return (
                            <Modal show centered onHide={() => setSizeModal(null)}>
                                <Modal.Header closeButton>
                                    <Modal.Title>
                                        <i className={`${item.icon} me-2`}></i>
                                        {item.label}
                                    </Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    {item.type === 'number' ? (
                                        <Form.Control
                                            size='lg'
                                            type='number'
                                            step='0.1'
                                            placeholder='e.g. 12.5'
                                            value={item.value}
                                            onChange={(e) => item.setValue(e.target.value)}
                                        />
                                    ) : item.type === 'date' ? (
                                        <Form.Control
                                            size='lg'
                                            type='date'
                                            value={item.value}
                                            onChange={(e) => item.setValue(e.target.value)}
                                        />
                                    ) : item.type === 'text' ? (
                                        <>
                                            {item.hint && <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>{item.hint}</div>}
                                            <Form.Control
                                                size='lg'
                                                type='text'
                                                placeholder={item.placeholder || ''}
                                                value={item.value}
                                                onChange={(e) => item.setValue(e.target.value)}
                                            />
                                        </>
                                    ) : (
                                        <Form.Select
                                            size='lg'
                                            value={item.value}
                                            onChange={(e) => item.setValue(e.target.value)}
                                        >
                                            {item.options.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </Form.Select>
                                    )}
                                    {item.extraFields && item.extraFields.map((ef, i) => (
                                        <div key={i} className='mt-3'>
                                            <Form.Label className='fw-semibold'>{ef.label}</Form.Label>
                                            {ef.type === 'text' ? (
                                                <Form.Control
                                                    size='lg'
                                                    type='text'
                                                    placeholder={ef.placeholder || ''}
                                                    value={ef.value}
                                                    onChange={(e) => ef.setValue(e.target.value)}
                                                />
                                            ) : (
                                                <Form.Select
                                                    size='lg'
                                                    value={ef.value}
                                                    onChange={(e) => ef.setValue(e.target.value)}
                                                >
                                                    {ef.options.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </Form.Select>
                                            )}
                                        </div>
                                    ))}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant='secondary' onClick={() => setSizeModal(null)}>Cancel</Button>
                                    <Button variant='primary' onClick={() => { tileSaveHandler(); setSizeModal(null) }}>Save</Button>
                                </Modal.Footer>
                            </Modal>
                        )
                    })()}

                    {/* ── Add Payment Modal ─────────────────────────────── */}
                    {(() => {
                        const selectedType = paymentTypes.find(pt => String(pt.id) === String(newPaymentTypeId))
                        const PAYMENT_DETAIL_CONFIG = {
                            'Venmo':       { label: 'Venmo Handle',    hint: 'Your @ username',          placeholder: '@username',         type: 'text' },
                            'Zelle':       { label: 'Zelle Phone',     hint: 'Phone number linked to Zelle', placeholder: '(702) 555-0000',    type: 'tel' },
                            'PayPal':      { label: 'PayPal Email',    hint: 'Email address on your PayPal', placeholder: 'you@email.com',     type: 'email' },
                            'Cash':        { label: 'Cash Note',       hint: 'Optional note (e.g. in person)', placeholder: 'e.g. In person only', type: 'text' },
                            'Credit Card': { label: 'Card Note',       hint: 'Optional note for the organizer', placeholder: 'e.g. Visa ending in 4242', type: 'text' },
                        }
                        const detailConfig = selectedType ? (PAYMENT_DETAIL_CONFIG[selectedType.name] || { label: 'Account Details', hint: '', placeholder: '', type: 'text' }) : null
                        return (
                            <Modal show={showPaymentModal} centered onHide={() => setShowPaymentModal(false)}>
                                <Modal.Header closeButton>
                                    <Modal.Title><i className='fa-solid fa-wallet me-2'></i>Add Payment Method</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Form.Group className='mb-3'>
                                        <Form.Label className='fw-semibold'>Payment Type</Form.Label>
                                        <Form.Select size='lg' value={newPaymentTypeId} onChange={(e) => { setNewPaymentTypeId(e.target.value); setNewAccountDetails('') }}>
                                            <option value=''>-- Select --</option>
                                            {paymentTypes.map(pt => (
                                                <option key={pt.id} value={pt.id}>{pt.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    {detailConfig && (
                                        <Form.Group>
                                            <Form.Label className='fw-semibold'>{detailConfig.label}</Form.Label>
                                            {detailConfig.hint && <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.4rem' }}>{detailConfig.hint}</div>}
                                            <Form.Control
                                                size='lg'
                                                type={detailConfig.type}
                                                placeholder={detailConfig.placeholder}
                                                value={newAccountDetails}
                                                onChange={(e) => setNewAccountDetails(e.target.value)}
                                            />
                                        </Form.Group>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant='secondary' onClick={() => setShowPaymentModal(false)}>Cancel</Button>
                                    <Button variant='primary' onClick={handleAddPayment} disabled={!newPaymentTypeId || !newAccountDetails.trim()}>Add</Button>
                                </Modal.Footer>
                            </Modal>
                        )
                    })()}

                    {/* ── Password Reset Modal ──────────────────────── */}
                    <Modal show={showPasswordModal} centered onHide={() => setShowPasswordModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title><i className='fa-solid fa-lock me-2'></i>Password Reset</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group controlId='password' className='mb-3'>
                                <Form.Label className='fw-semibold'>New Password</Form.Label>
                                <Form.Control
                                    size='lg'
                                    type='password'
                                    placeholder='Leave blank to keep current'
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group controlId='passwordConfirm'>
                                <Form.Label className='fw-semibold'>Confirm Password</Form.Label>
                                <Form.Control
                                    size='lg'
                                    type='password'
                                    placeholder='Confirm new password'
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            {messageError && <span className='text-danger me-auto small'>{messageError}</span>}
                            <Button variant='secondary' onClick={() => setShowPasswordModal(false)}>Cancel</Button>
                            <Button
                                variant='primary'
                                onClick={credentialsSaveHandler}
                                disabled={savingSection === 'credentials'}
                            >
                                {savingSection === 'credentials'
                                    ? <><Spinner size='sm' animation='border' className='me-1' />Saving...</>
                                    : 'Save Password'}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                </Form>
            </Col>
        </Row>
    )
}

export default ProfileScreen
